import { updateOrganization } from '../../../api/remiApi';
import { updateStorefront, type ConsentStyle } from '../../../api/storefront';
import {
  BrandingFooter,
  BrandingPropertyDetails,
  BrandingSqueezePage,
} from '../../../types/branding';

interface BrandingData {
  colors: {
    primary: string;
    secondary: string;
  };
  logo: {
    url: string;
    width: number;
    height: number;
    placement?: 'header' | 'footer' | 'both';
  };
  favicon?: {
    url?: string;
    width?: number;
    height?: number;
  };
  // Extended branding sections (round-tripped via tenant_config on the Storefront svc)
  teamName?: string;
  brokerageName?: string;
  footer?: BrandingFooter;
  propertyDetails?: BrandingPropertyDetails;
  squeezePage?: BrandingSqueezePage;
}

interface OrgLink {
  key: string;
  value: string;
}

export const useUpdateBranding = () => {
  const updateBranding = async (
    organizationId: string,
    branding: BrandingData,
    orgLinks: OrgLink[],
    // SPEC-2232: lifted out of the branding blob because it lives in the dedicated
    // Organizations.idx_consent_style column, not tenant_config. Undefined leaves
    // the column untouched; null clears it back to the IDX allowlist fallback.
    idxConsentStyle?: ConsentStyle | null
  ) => {
    if (!organizationId) throw new Error('Organization ID is required');

    // Branding is owned by the Storefront service (Spanner) — replaces the old
    // Firestore `tenants` doc write. Sends the full branding tree; the service maps
    // colors/logo/fonts/favicon to columns and stashes the rest in tenant_config.
    await updateStorefront(organizationId, {
      branding,
      ...(idxConsentStyle !== undefined ? { idxConsentStyle } : {}),
    });

    // org_links (social links) stays org-core on remi-api (Spanner).
    const org_links = orgLinks.reduce(
      (acc, link) => ({ ...acc, [link.key]: link.value }),
      {}
    );
    await updateOrganization(organizationId, { org_links });
  };

  return { updateBranding };
};
